import React from 'react';
import PolicyLayout from './PolicyLayout';

const TermsOfService = () => {
  return (
    <PolicyLayout title="Terms of Service" lastUpdated="January 20, 2026">
      <p>
        Welcome to AppealBot. By accessing or using our platform, you agree to be bound by these
        Terms of Service ("Terms"). Please read them carefully before using our services.
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using AppealBot's services, you acknowledge that you have read,
        understood, and agree to be bound by these Terms and our Privacy Policy. If you do not
        agree to these Terms, you may not access or use our services.
      </p>

      <h2>2. Description of Services</h2>
      <p>
        AppealBot provides an automated platform for healthcare providers to manage insurance
        claims and generate appeals for denied claims. Our services include:
      </p>
      <ul>
        <li>Claims management and tracking</li>
        <li>AI-powered appeal letter generation</li>
        <li>Document storage and management</li>
        <li>Integration with clearinghouses and payers</li>
        <li>Analytics and reporting tools</li>
      </ul>

      <h2>3. Eligibility</h2>
      <p>
        To use AppealBot, you must be:
      </p>
      <ul>
        <li>At least 18 years of age</li>
        <li>A licensed healthcare provider or authorized representative of a healthcare practice</li>
        <li>Capable of forming a binding contract</li>
        <li>Not barred from receiving services under applicable law</li>
      </ul>

      <h2>4. Account Registration</h2>
      <p>
        You must register for an account to use our services. You agree to:
      </p>
      <ul>
        <li>Provide accurate and complete registration information</li>
        <li>Maintain the security of your account credentials</li>
        <li>Promptly update any changes to your information</li>
        <li>Accept responsibility for all activities under your account</li>
        <li>Notify us immediately of any unauthorized access</li>
      </ul>

      <h2>5. Acceptable Use</h2>
      <p>
        You agree to use AppealBot only for lawful purposes and in accordance with these Terms.
        You shall not:
      </p>
      <ul>
        <li>Submit false, misleading, or fraudulent claims or appeals</li>
        <li>Upload malicious code or attempt to compromise our systems</li>
        <li>Access accounts or data belonging to other users</li>
        <li>Use the service to violate any applicable laws or regulations</li>
        <li>Resell or redistribute our services without authorization</li>
        <li>Interfere with or disrupt the integrity of our platform</li>
      </ul>

      <h2>6. Healthcare Compliance</h2>
      <p>
        As a healthcare technology provider, we take compliance seriously. You acknowledge that:
      </p>
      <ul>
        <li>You are responsible for ensuring the accuracy of all information submitted</li>
        <li>Appeals generated by our platform should be reviewed before submission</li>
        <li>You remain responsible for compliance with payer requirements</li>
        <li>Our AI-generated content is a tool, not a substitute for professional judgment</li>
        <li>You must comply with all applicable healthcare laws and regulations</li>
      </ul>

      <h2>7. Protected Health Information</h2>
      <p>
        AppealBot processes Protected Health Information (PHI) on behalf of healthcare providers.
        Our handling of PHI is governed by our Business Associate Agreement and applicable HIPAA
        regulations. By using our services, you agree to enter into a Business Associate Agreement
        with AppealBot.
      </p>

      <h2>8. Fees and Payment</h2>
      <p>
        Certain features of AppealBot require payment of fees. You agree to:
      </p>
      <ul>
        <li>Pay all applicable fees according to your selected plan</li>
        <li>Provide accurate billing information</li>
        <li>Authorize us to charge your payment method</li>
        <li>Update payment information as needed to ensure uninterrupted service</li>
      </ul>
      <p>
        Fees are non-refundable except as required by law or as explicitly stated in these Terms.
        We reserve the right to modify pricing with 30 days' notice.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        AppealBot and its content, features, and functionality are owned by AppealBot and are
        protected by copyright, trademark, and other intellectual property laws. You retain
        ownership of any data you submit to the platform, but grant us a limited license to
        use it to provide our services.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        AppealBot is provided "as is" without warranties of any kind, express or implied. We
        do not guarantee:
      </p>
      <ul>
        <li>That appeals will be successful or claims will be paid</li>
        <li>Uninterrupted or error-free service</li>
        <li>That our AI-generated content will be accurate or complete</li>
        <li>Compatibility with all payer systems or requirements</li>
      </ul>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, AppealBot shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages, including loss of profits, data,
        or other intangible losses, resulting from your use of our services.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless AppealBot and its officers, directors, employees,
        and agents from any claims, damages, or expenses arising from your use of the service,
        violation of these Terms, or infringement of any third-party rights.
      </p>

      <h2>13. Termination</h2>
      <p>
        Either party may terminate this agreement at any time. Upon termination:
      </p>
      <ul>
        <li>Your access to the service will be revoked</li>
        <li>You may request export of your data within 30 days</li>
        <li>We will retain data as required by law and our data retention policy</li>
        <li>Outstanding fees remain payable</li>
      </ul>

      <h2>14. Changes to Terms</h2>
      <p>
        We may modify these Terms at any time. We will notify you of material changes via email
        or through the platform. Continued use after changes constitutes acceptance of the
        modified Terms.
      </p>

      <h2>15. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State
        of Delaware, without regard to its conflict of law provisions.
      </p>

      <h2>16. Dispute Resolution</h2>
      <p>
        Any disputes arising from these Terms or your use of AppealBot shall be resolved through
        binding arbitration in accordance with the American Arbitration Association rules, except
        where prohibited by law.
      </p>

      <h2>17. Contact Information</h2>
      <p>
        For questions about these Terms, please contact us at:
      </p>
      <ul>
        <li>Email: legal@appealbot.com</li>
        <li>Address: AppealBot, Inc.</li>
      </ul>
    </PolicyLayout>
  );
};

export default TermsOfService;
